import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from 'recharts';
const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];
function getChartConfig(widget) {
    try {
        return JSON.parse(widget.chartConfig);
    }
    catch (_a) {
        return {};
    }
}
/** Pivot data by a color column: each unique value in colorCol becomes its own series key */
function pivotByColor(data, labelKey, valueKey, colorCol) {
    var _a, _b;
    const seriesSet = new Set();
    const grouped = new Map();
    for (const row of data) {
        const label = String((_a = row[labelKey]) !== null && _a !== void 0 ? _a : '');
        const colorVal = String((_b = row[colorCol]) !== null && _b !== void 0 ? _b : '');
        const numVal = row[valueKey];
        seriesSet.add(colorVal);
        if (!grouped.has(label))
            grouped.set(label, { [labelKey]: row[labelKey] });
        grouped.get(label)[colorVal] = numVal;
    }
    return { pivoted: Array.from(grouped.values()), series: Array.from(seriesSet) };
}
function formatMetricValue(val) {
    if (val == null)
        return '-';
    if (typeof val === 'number') {
        if (Number.isInteger(val))
            return val.toLocaleString('en-US');
        return val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
    const num = Number(val);
    if (!isNaN(num) && String(val).trim() !== '') {
        if (Number.isInteger(num))
            return num.toLocaleString('en-US');
        return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
    return String(val);
}
const MetricWidget = ({ data, keys, colors, name }) => {
    const ref = useRef(null);
    const [fontSize, setFontSize] = useState(40);
    const row = data[0];
    const numKey = keys.find(k => typeof row[k] === 'number') || keys[keys.length - 1];
    const val = row[numKey];
    const formatted = formatMetricValue(val);
    const charCountRef = useRef(formatted.length);
    charCountRef.current = formatted.length;
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const ro = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            const baseSize = Math.min(height * 0.4, width * 0.15);
            const charBasedMax = (width * 0.85) / (charCountRef.current * 0.6);
            setFontSize(Math.max(16, Math.min(96, Math.min(baseSize, charBasedMax))));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const labelSize = Math.max(11, Math.min(19, fontSize * 0.35));
    return (_jsxs("div", { ref: ref, className: "d-flex flex-column align-items-center justify-content-center h-100", children: [_jsx("div", { style: {
                    fontSize, fontWeight: 700, color: colors[0], lineHeight: 1.1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: '100%', textAlign: 'center', padding: '0 4px'
                }, children: formatted }), _jsx("div", { className: "text-muted mt-1", style: {
                    fontSize: labelSize, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', maxWidth: '100%', textAlign: 'center'
                }, children: name })] }));
};
const SortableTable = ({ data, keys, onDrillThrough }) => {
    const [sort, setSort] = useState(null);
    const toggleSort = (col) => {
        setSort(prev => {
            if ((prev === null || prev === void 0 ? void 0 : prev.col) === col) {
                return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
            }
            return { col, dir: 'asc' };
        });
    };
    const sortIcon = (col) => {
        if ((sort === null || sort === void 0 ? void 0 : sort.col) !== col)
            return 'bi-chevron-expand';
        return sort.dir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
    };
    const sortedData = useMemo(() => {
        if (!sort)
            return data;
        const { col, dir } = sort;
        return [...data].sort((a, b) => {
            const aVal = a[col];
            const bVal = b[col];
            if (aVal == null && bVal == null)
                return 0;
            if (aVal == null)
                return dir === 'asc' ? -1 : 1;
            if (bVal == null)
                return dir === 'asc' ? 1 : -1;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return dir === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal));
            return dir === 'asc' ? cmp : -cmp;
        });
    }, [data, sort]);
    return (_jsx("div", { style: { overflow: 'auto', height: '100%' }, children: _jsxs("table", { className: "table table-sm table-hover table-bordered mb-0", children: [_jsx("thead", { children: _jsx("tr", { children: keys.map(k => (_jsxs("th", { className: "small text-nowrap", style: { cursor: 'pointer', userSelect: 'none' }, onClick: () => toggleSort(k), children: [k, _jsx("i", { className: `bi ${sortIcon(k)} ms-1`, style: { fontSize: '0.65rem' } })] }, k))) }) }), _jsx("tbody", { children: sortedData.map((row, i) => (_jsx("tr", { style: onDrillThrough ? { cursor: 'pointer' } : undefined, onClick: onDrillThrough ? () => onDrillThrough(row) : undefined, children: keys.map(k => _jsx("td", { className: "small", children: row[k] == null ? '' : String(row[k]) }, k)) }, i))) })] }) }));
};
const WidgetRenderer = ({ widget, data, loading, error, onDrillThrough }) => {
    var _a;
    if (loading) {
        return (_jsxs("div", { className: "d-flex align-items-center justify-content-center h-100", children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2" }), " Loading..."] }));
    }
    if (error) {
        return _jsx("div", { className: "d-flex align-items-center justify-content-center h-100 text-danger small", children: error });
    }
    if (!data || data.length === 0) {
        return _jsx("div", { className: "d-flex align-items-center justify-content-center h-100 text-muted small", children: "No data" });
    }
    const config = getChartConfig(widget);
    const keys = Object.keys(data[0]).filter(k => k !== '_id');
    // Use configured axis columns or fall back to auto-detection
    const xAxis = config.xAxisColumn;
    const yAxis = config.yAxisColumn;
    const colorCol = config.colorColumn;
    const labelKey = (xAxis && keys.includes(xAxis)) ? xAxis : keys[0] || '';
    // Pivot by color column if configured
    const hasPivot = !!(colorCol && keys.includes(colorCol) && colorCol !== labelKey);
    const valKey = (yAxis && keys.includes(yAxis)) ? yAxis : keys.find(k => k !== labelKey && k !== colorCol) || 'value';
    const pivotResult = hasPivot && colorCol ? pivotByColor(data, labelKey, valKey, colorCol) : null;
    const chartData = pivotResult ? pivotResult.pivoted : data;
    const valueKeys = pivotResult
        ? pivotResult.series
        : (yAxis && keys.includes(yAxis))
            ? [yAxis]
            : keys.filter(k => k !== labelKey);
    // Auto-color: use COLORS array for multi-series, user color for single series
    const userColor = (_a = config.colors) === null || _a === void 0 ? void 0 : _a[0];
    const colors = valueKeys.length > 1 ? COLORS : (userColor ? [userColor, ...COLORS.slice(1)] : COLORS);
    const chartCursor = onDrillThrough ? 'pointer' : undefined;
    // activeLabel is the clicked category, row[labelKey]
    const handleChartClick = (state) => {
        if (!onDrillThrough || state.activeLabel === undefined)
            return;
        onDrillThrough({ [labelKey]: state.activeLabel });
    };
    const handlePieClick = (_, index) => {
        if (!onDrillThrough || !data[index])
            return;
        const entry = data[index];
        onDrillThrough({ [labelKey]: entry[labelKey] });
    };
    if (widget.widgetType === 'metric') {
        return _jsx(MetricWidget, { data: data, keys: keys, colors: colors, name: widget.name });
    }
    if (widget.widgetType === 'table') {
        return _jsx(SortableTable, { data: data, keys: keys, onDrillThrough: onDrillThrough });
    }
    if (widget.widgetType === 'bar') {
        // Use per-bar coloring when colorful flag is set or single value series without pivot
        const colorful = !hasPivot && (config.colorful === true || valueKeys.length === 1);
        return (_jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(BarChart, { data: chartData, margin: { top: 5, right: 20, left: 0, bottom: 5 }, onClick: handleChartClick, style: chartCursor ? { cursor: chartCursor } : undefined, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3" }), _jsx(XAxis, { dataKey: labelKey, tick: { fontSize: 11 } }), _jsx(YAxis, { tick: { fontSize: 11 } }), _jsx(Tooltip, {}), valueKeys.length > 1 && _jsx(Legend, {}), valueKeys.map((k, i) => (_jsx(Bar, { dataKey: k, fill: colors[i % colors.length], style: chartCursor ? { cursor: chartCursor } : undefined, children: colorful && chartData.map((_, j) => (_jsx(Cell, { fill: colors[j % colors.length] }, j))) }, k)))] }) }));
    }
    if (widget.widgetType === 'line') {
        return (_jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(LineChart, { data: chartData, margin: { top: 5, right: 20, left: 0, bottom: 5 }, onClick: handleChartClick, style: chartCursor ? { cursor: chartCursor } : undefined, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3" }), _jsx(XAxis, { dataKey: labelKey, tick: { fontSize: 11 } }), _jsx(YAxis, { tick: { fontSize: 11 } }), _jsx(Tooltip, {}), valueKeys.length > 1 && _jsx(Legend, {}), valueKeys.map((k, i) => (_jsx(Line, { type: "monotone", dataKey: k, stroke: colors[i % colors.length], style: chartCursor ? { cursor: chartCursor } : undefined }, k)))] }) }));
    }
    if (widget.widgetType === 'area') {
        return (_jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(AreaChart, { data: chartData, margin: { top: 5, right: 20, left: 0, bottom: 5 }, onClick: handleChartClick, style: chartCursor ? { cursor: chartCursor } : undefined, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3" }), _jsx(XAxis, { dataKey: labelKey, tick: { fontSize: 11 } }), _jsx(YAxis, { tick: { fontSize: 11 } }), _jsx(Tooltip, {}), valueKeys.length > 1 && _jsx(Legend, {}), valueKeys.map((k, i) => (_jsx(Area, { type: "monotone", dataKey: k, fill: colors[i % colors.length], stroke: colors[i % colors.length], fillOpacity: 0.3, style: chartCursor ? { cursor: chartCursor } : undefined }, k)))] }) }));
    }
    if (widget.widgetType === 'pie' || widget.widgetType === 'doughnut') {
        const valueKey = valueKeys[0] || 'value';
        const innerRadius = widget.widgetType === 'doughnut' ? '40%' : 0;
        const renderLabel = ({ name, percent }) => {
            if ((percent || 0) < 0.05)
                return null;
            return `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`;
        };
        return (_jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(PieChart, { margin: { top: 5, right: 5, bottom: 5, left: 5 }, children: [_jsx(Pie, { data: data, dataKey: valueKey, nameKey: labelKey, cx: "50%", cy: "50%", innerRadius: innerRadius, outerRadius: "60%", label: renderLabel, labelLine: false, fontSize: 11, onClick: handlePieClick, style: chartCursor ? { cursor: chartCursor } : undefined, children: data.map((_, i) => (_jsx(Cell, { fill: colors[i % colors.length], style: chartCursor ? { cursor: chartCursor } : undefined }, i))) }), _jsx(Tooltip, { formatter: (value) => typeof value === 'number' ? value.toLocaleString() : String(value) }), _jsx(Legend, { wrapperStyle: { fontSize: '0.7rem' } })] }) }));
    }
    return _jsxs("div", { className: "text-muted small p-2", children: ["Unknown widget type: ", widget.widgetType] });
};
export default WidgetRenderer;
